import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const QUESTIONS = [
  "Why do you want to study this course or subject?",
  "How have your qualifications and studies prepared you for it?",
  "What else have you done outside formal education, and why is it useful?",
];

export const MIN_PER_QUESTION = 350;
export const MAX_TOTAL = 4000;

export const getConfig = () => axios.get(`${API}/config`).then((r) => r.data);

export const createSubmission = (data) =>
  axios
    .post(`${API}/submissions`, data, { timeout: 90000 })
    .then((r) => r.data);

export const getSubmission = (id) =>
  axios.get(`${API}/submissions/${id}`).then((r) => r.data);

// Band metadata for the results ring (band string -> color + fill fraction)
export const BANDS = {
  "Needs work": { color: "#C8553D", fraction: 0.25 },
  "Getting there": { color: "#D97706", fraction: 0.5 },
  Strong: { color: "#2D6A4F", fraction: 0.78 },
  Standout: { color: "#2D6A4F", fraction: 1 },
};
export const bandMeta = (b) => BANDS[b] || BANDS["Needs work"];

// ---- Admin auth (JWT Bearer) ----
// Token is kept in sessionStorage (cleared when the tab closes) rather than
// localStorage to reduce its exposure window.
const TOKEN_KEY = "sc_admin_token";
export const getToken = () => sessionStorage.getItem(TOKEN_KEY);
export const setToken = (t) => sessionStorage.setItem(TOKEN_KEY, t);
export const clearToken = () => sessionStorage.removeItem(TOKEN_KEY);
const authHeaders = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });

// Global 401 handler: an expired/idle/revoked session bounces the admin to login.
axios.interceptors.response.use(
  (r) => r,
  (err) => {
    const status = err?.response?.status;
    const path = window.location.pathname;
    if (status === 401 && getToken() && path.startsWith("/admin")) {
      clearToken();
      if (path !== "/admin-login") {
        window.location.assign("/admin-login?expired=1");
      }
    }
    return Promise.reject(err);
  }
);

export const adminLogin = (payload) =>
  axios.post(`${API}/auth/login`, payload).then((r) => r.data);
export const adminLogout = () =>
  axios.post(`${API}/auth/logout`, {}, authHeaders()).catch(() => {});
export const adminMe = () => axios.get(`${API}/auth/me`, authHeaders()).then((r) => r.data);
export const getRubric = () => axios.get(`${API}/admin/rubric`, authHeaders()).then((r) => r.data);
export const saveRubric = (rubric) =>
  axios.put(`${API}/admin/rubric`, { rubric }, authHeaders()).then((r) => r.data);
export const getAiEnabled = () => axios.get(`${API}/admin/ai-enabled`, authHeaders()).then((r) => r.data);
export const setAiEnabled = (enabled) =>
  axios.put(`${API}/admin/ai-enabled`, { enabled }, authHeaders()).then((r) => r.data);
export const getAnalytics = () => axios.get(`${API}/admin/analytics`, authHeaders()).then((r) => r.data);
export const changePassword = (current_password, new_password) =>
  axios.post(`${API}/admin/change-password`, { current_password, new_password }, authHeaders()).then((r) => r.data);
export const get2faSetup = () => axios.post(`${API}/admin/2fa/setup`, {}, authHeaders()).then((r) => r.data);
export const enable2fa = (code) =>
  axios.post(`${API}/admin/2fa/enable`, { code }, authHeaders()).then((r) => r.data);
export const disable2fa = (code) =>
  axios.post(`${API}/admin/2fa/disable`, { code }, authHeaders()).then((r) => r.data);

// ---- Ratings ----
export const submitRating = (id, rating) =>
  axios.post(`${API}/submissions/${id}/rating`, { rating }).then((r) => r.data);

// ---- Payments & coupons (public) ----
export const validateCoupon = (code) =>
  axios.post(`${API}/coupons/validate`, { code }).then((r) => r.data);
export const startCheckout = (job_id, origin_url, coupon_code) =>
  axios
    .post(`${API}/payments/checkout`, { job_id, origin_url, coupon_code })
    .then((r) => r.data);
export const getPaymentStatus = (session_id) =>
  axios.get(`${API}/payments/status/${session_id}`).then((r) => r.data);

export const UNLOCK_PRICE = 10;

// ---- Admin coupons ----
export const getCoupons = () =>
  axios.get(`${API}/admin/coupons`, authHeaders()).then((r) => r.data);
export const createCoupon = (data) =>
  axios.post(`${API}/admin/coupons`, data, authHeaders()).then((r) => r.data);
export const deleteCoupon = (code) =>
  axios.delete(`${API}/admin/coupons/${code}`, authHeaders()).then((r) => r.data);
export const reinstateCoupon = (code) =>
  axios.post(`${API}/admin/coupons/${code}/reinstate`, {}, authHeaders()).then((r) => r.data);

// ---- Admin management (owner only) ----
export const getAdmins = () =>
  axios.get(`${API}/admin/admins`, authHeaders()).then((r) => r.data);
export const createAdmin = (data) =>
  axios.post(`${API}/admin/admins`, data, authHeaders()).then((r) => r.data);
export const deleteAdmin = (email) =>
  axios.delete(`${API}/admin/admins/${encodeURIComponent(email)}`, authHeaders()).then((r) => r.data);

// ---- Expert guides (public) ----
export const getGuides = () => axios.get(`${API}/guides`).then((r) => r.data);
export const getGuide = (slug) => axios.get(`${API}/guides/${slug}`).then((r) => r.data);

// ---- Expert guides (admin: owner or editor) ----
export const getAdminGuides = () => axios.get(`${API}/admin/guides`, authHeaders()).then((r) => r.data);
export const createGuide = (data) => axios.post(`${API}/admin/guides`, data, authHeaders()).then((r) => r.data);
export const updateGuide = (id, data) => axios.put(`${API}/admin/guides/${id}`, data, authHeaders()).then((r) => r.data);
export const deleteGuide = (id) => axios.delete(`${API}/admin/guides/${id}`, authHeaders()).then((r) => r.data);

// ---- Pricing (public) ----
export const getPricing = () => axios.get(`${API}/pricing`).then((r) => r.data);

// ---- Editable legal pages (public) ----
export const getLegal = (page) => axios.get(`${API}/legal/${page}`).then((r) => r.data);

// ---- Recommend / testimonials (public, post-unlock) ----
export const recommendFeedback = (id, data) =>
  axios.post(`${API}/submissions/${id}/recommend`, data).then((r) => r.data);

// ---- Guide funnel attribution (anonymous, session-level) ----
const GUIDE_REF_KEY = "sm_guide_ref";
export const setGuideRef = (slug) => {
  try { sessionStorage.setItem(GUIDE_REF_KEY, slug); } catch { /* ignore */ }
};
export const getGuideRef = () => {
  try { return sessionStorage.getItem(GUIDE_REF_KEY) || null; } catch { return null; }
};
export const guideClickthrough = (slug) =>
  axios.post(`${API}/guides/${slug}/clickthrough`).catch((e) => {
    if (process.env.NODE_ENV !== "production") console.error("clickthrough failed", e);
  });

// ---- Admin: pricing & sales (owner) ----
export const getAdminPricing = () => axios.get(`${API}/admin/pricing`, authHeaders()).then((r) => r.data);
export const setAdminPricing = (data) => axios.put(`${API}/admin/pricing`, data, authHeaders()).then((r) => r.data);
export const getRetention = () => axios.get(`${API}/admin/retention`, authHeaders()).then((r) => r.data);
export const setRetention = (months) => axios.put(`${API}/admin/retention`, { months }, authHeaders()).then((r) => r.data);

// ---- Admin: editable legal pages (owner) ----
export const saveLegal = (page, html) => axios.put(`${API}/admin/legal/${page}`, { html }, authHeaders()).then((r) => r.data);

// ---- Admin: feedback log (owner) ----
export const getFeedbackLog = (skip = 0, limit = 30) =>
  axios.get(`${API}/admin/feedback-log?skip=${skip}&limit=${limit}`, authHeaders()).then((r) => r.data);
export const getFeedbackDetail = (id) =>
  axios.get(`${API}/admin/feedback-log/${id}`, authHeaders()).then((r) => r.data);
export const downloadFeedbackPdf = (id) =>
  axios.get(`${API}/admin/feedback-log/${id}/pdf`, { ...authHeaders(), responseType: "blob" }).then((r) => r.data);
export const getRecommendations = () =>
  axios.get(`${API}/admin/recommendations`, authHeaders()).then((r) => r.data);
